import type { FeatureFlagService } from "../../../services/feature-flags";
import { buildTraceScene } from "./model";
import {
  msPerCharacterAt,
  pathPoints,
  phaseWalkFor,
  phaseWalkFrame,
} from "./phase-walk";
import { parseTraceSidecarText, sidecarMatchesImage } from "./sidecar";
import type {
  TraceAssetSelection,
  TracePaneHandle,
  TracePickerController,
  TraceScenePath,
  TraceScenePoint,
} from "./types";

/** A trace pane that does nothing, for every build where it should not. */
const INERT_PANE: TracePaneHandle = Object.freeze({
  deliveryReached: () => undefined,
  dispose: () => undefined,
});

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

function svgElement<K extends keyof SVGElementTagNameMap>(
  name: K,
): SVGElementTagNameMap[K] {
  return document.createElementNS(SVG_NAMESPACE, name);
}

function pointGlyph(point: TraceScenePoint): SVGGElement {
  const group = svgElement("g");
  group.dataset.pointId = point.id;
  group.dataset.kind = point.kind;
  group.dataset.type = point.type;
  group.setAttribute(
    "transform",
    `translate(${String(point.x)} ${String(point.y)})`,
  );
  const marker = svgElement("circle");
  marker.setAttribute("r", point.kind === "marker" ? "10" : "7");
  marker.setAttribute("class", `trace-color-${point.color}`);
  const label = svgElement("text");
  label.setAttribute("x", "14");
  label.setAttribute("y", "-12");
  label.textContent = point.label;
  group.append(marker, label);
  return group;
}

function overlayFor(selection: TraceAssetSelection): SVGSVGElement | null {
  if (selection.sidecar === null) return null;
  const scene = buildTraceScene(selection.sidecar);
  const overlay = svgElement("svg");
  overlay.classList.add("trace-overlay");
  overlay.dataset.role = "trace-overlay";
  overlay.setAttribute("viewBox", "0 0 1000 1000");
  overlay.setAttribute("preserveAspectRatio", "none");
  overlay.setAttribute("aria-label", "Trace overlay");
  for (const path of scene.paths) {
    const line = svgElement("polyline");
    line.dataset.phaseId = path.id;
    line.dataset.bindItemId = path.bindItemId;
    line.setAttribute("points", path.points);
    line.setAttribute("class", "trace-phase-path");
    overlay.append(line);
  }
  for (const point of scene.points) overlay.append(pointGlyph(point));
  return overlay;
}

/**
 * The ring that walks a phase path while its narrative is spoken.
 *
 * It is a presentation detail on purpose: every decision about where the
 * pointer is and when it has arrived is made by the pure functions in
 * `phase-walk.ts`, which are tested. This holds a circle, a clock and a
 * cancellation, and nothing that has to be reasoned about.
 */
function pointerRing(overlay: SVGSVGElement): {
  readonly stop: () => void;
  readonly walk: (path: TraceScenePath, targetWpm: number) => void;
} {
  const ring = svgElement("circle");
  ring.dataset.role = "trace-phase-pointer";
  ring.setAttribute("r", "18");
  ring.setAttribute("class", "trace-phase-pointer");
  ring.setAttribute("visibility", "hidden");
  overlay.append(ring);

  let frame: number | null = null;
  const stop = (): void => {
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    ring.setAttribute("visibility", "hidden");
  };

  const walk = (path: TraceScenePath, targetWpm: number): void => {
    stop();
    const points = pathPoints(path);
    if (points.length === 0) return;
    const msPerCharacter = msPerCharacterAt(targetWpm);
    const started = performance.now();
    ring.setAttribute("visibility", "visible");
    const step = (now: number): void => {
      const state = phaseWalkFrame(path, now - started, msPerCharacter);
      ring.setAttribute("cx", String(state.pointer.x));
      ring.setAttribute("cy", String(state.pointer.y));
      ring.dataset.progress = state.progress.toFixed(3);
      frame = state.complete ? null : requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
  };

  return { stop, walk };
}

export function renderTraceSelection(
  target: HTMLElement,
  selection: TraceAssetSelection,
  flags: FeatureFlagService,
  targetWpm = 0,
): TracePaneHandle {
  target.replaceChildren();
  if (!flags.isEnabled("trace")) {
    target.append(element("p", "Trace rendering is disabled in this build."));
    return INERT_PANE;
  }
  if (selection.imageUrl === null) {
    target.append(
      element(
        "p",
        "No trace image selected. Delivery will use the text-only fallback.",
      ),
    );
    return INERT_PANE;
  }

  const figure = element("figure");
  figure.className = "trace-figure";
  const image = element("img");
  image.src = selection.imageUrl;
  image.alt = "Locally selected trace";
  image.dataset.role = "trace-rendered-image";
  image.addEventListener(
    "error",
    () => {
      figure.remove();
      target.prepend(
        element(
          "p",
          "The selected trace image could not be decoded by this browser.",
        ),
      );
    },
    { once: true },
  );
  figure.append(image);
  const sidecarMatches =
    selection.sidecar === null ||
    selection.imageName === null ||
    sidecarMatchesImage(selection.sidecar.image, selection.imageName);
  const overlay = sidecarMatches ? overlayFor(selection) : null;
  if (overlay !== null) figure.append(overlay);
  target.append(figure);
  const scene =
    overlay !== null && selection.sidecar !== null
      ? buildTraceScene(selection.sidecar)
      : null;
  if (selection.sidecar === null) {
    target.append(
      element(
        "p",
        "Trace image loaded. No optional overlay selected; markers and paths are skipped.",
      ),
    );
  } else if (!sidecarMatches) {
    target.append(
      element(
        "p",
        "The optional overlay names a different image, so it was not applied.",
      ),
    );
  }

  if (overlay === null || scene === null) return INERT_PANE;

  const ring = pointerRing(overlay);
  return {
    deliveryReached: (itemId) => {
      // The flag is read here, on every call, rather than once at mount. A
      // pane built before the flag was consulted must still do nothing.
      const walk = phaseWalkFor(scene, itemId, flags);
      const [first] = walk.phases;
      const showing = new Set(walk.markerIds);
      for (const glyph of overlay.querySelectorAll("g[data-point-id]")) {
        const id = (glyph as SVGGElement).dataset.pointId ?? "";
        (glyph as SVGGElement).dataset.phaseActive = String(showing.has(id));
      }
      for (const line of overlay.querySelectorAll("polyline[data-phase-id]")) {
        const bound = (line as SVGPolylineElement).dataset.bindItemId;
        (line as SVGPolylineElement).dataset.phaseActive = String(
          walk.phases.some((phase) => phase.bindItemId === bound),
        );
      }
      if (first === undefined) {
        ring.stop();
        return;
      }
      ring.walk(first, targetWpm);
    },
    dispose: ring.stop,
  };
}

export function mountTracePicker(
  target: HTMLElement,
  flags: FeatureFlagService,
): TracePickerController {
  let imageFile: File | null = null;
  let imageName: string | null = null;
  let imageUrl: string | null = null;
  let sidecar = null as TraceAssetSelection["sidecar"];

  const imageLabel = element("label", "Trace image ");
  const imageInput = element("input");
  imageInput.type = "file";
  imageInput.accept = "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp";
  imageInput.dataset.role = "trace-image";
  imageLabel.append(imageInput);

  const sidecarLabel = element("label", "Optional overlay file (.json) ");
  const sidecarInput = element("input");
  sidecarInput.type = "file";
  sidecarInput.accept = "application/json,.json";
  sidecarInput.dataset.role = "trace-sidecar";
  sidecarLabel.append(sidecarInput);

  const status = element("p");
  status.dataset.role = "trace-status";
  status.textContent = "The trace image works without an overlay file.";
  const preview = element("div");
  preview.className = "trace-preview";
  preview.dataset.role = "trace-preview";

  const selection = (): TraceAssetSelection => ({
    imageFile,
    imageName,
    imageUrl,
    sidecar,
  });
  const render = (): void => {
    renderTraceSelection(preview, selection(), flags);
  };

  imageInput.addEventListener("change", () => {
    const file = imageInput.files?.item(0);
    if (file === null || file === undefined) return;
    if (imageUrl !== null) URL.revokeObjectURL(imageUrl);
    imageFile = file;
    imageName = file.name;
    imageUrl = URL.createObjectURL(file);
    status.textContent =
      sidecar !== null && !sidecarMatchesImage(sidecar.image, file.name)
        ? "The selected optional overlay names a different image."
        : "Trace image loaded locally.";
    status.dataset.status =
      sidecar !== null && !sidecarMatchesImage(sidecar.image, file.name)
        ? "error"
        : "ready";
    render();
  });

  sidecarInput.addEventListener("change", () => {
    const file = sidecarInput.files?.item(0);
    if (file === null || file === undefined) return;
    status.textContent = "Reading local optional overlay...";
    void file
      .text()
      .then((serialized) => {
        const parsed = parseTraceSidecarText(serialized);
        if (!parsed.ok) {
          sidecar = null;
          status.textContent = parsed.error.message;
          status.dataset.status = "error";
          render();
          return;
        }
        sidecar = parsed.value;
        const matches =
          imageName === null || sidecarMatchesImage(sidecar.image, imageName);
        status.textContent = matches
          ? "Optional overlay loaded locally."
          : "The selected optional overlay names a different image.";
        status.dataset.status = matches ? "ready" : "error";
        render();
      })
      .catch(() => {
        sidecar = null;
        status.textContent = "The local optional overlay could not be read.";
        status.dataset.status = "error";
        render();
      });
  });

  target.append(imageLabel, sidecarLabel, status, preview);
  render();
  return {
    dispose: () => {
      if (imageUrl !== null) URL.revokeObjectURL(imageUrl);
      imageUrl = null;
      imageFile = null;
      imageName = null;
      sidecar = null;
    },
    selection,
  };
}
